import React, {
  FC,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Button,
  Input,
  NumberInput,
  Toggle,
  TextArea,
  BackwardIcon,
  Preloader,
  Tooltip,
  H3,
  H2,
  Select,
  Separator,
  BucketIcon,
  InfoBlock,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { NavLink, useNavigate, useParams } from 'react-router-dom';

import { useIsCashboxEnabled } from 'cashbox';
import { NDSSelect } from 'components';
import { FeatureHOC } from 'features';
import { useGoodsRequests, useCategoryRequests, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { ManagerWarehouseModal } from 'modals';
import { openConfirmDialog } from 'services/Confirmation';
import notificationService from 'services/notificationService';
import { SettingsContext } from 'settings';
import { TGood } from 'types';

import { NotificationDelete } from '../Goods/NotificationDelete';
import { EditPhoto } from './components';
import { DEFAULT_VALUES } from './constants';
import s from './GoodManager.module.scss';
import { IFormValues } from './interfaces';

export const GoodManager: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency, telegramChannelId },
    },
  } = useContext(SettingsContext);
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const paramID = params.id === 'new' ? null : Number(params.id);

  const [good, setGood] = useState<TGood>();
  const [isGoodDeleting, setIsGoodDeleting] = useState(false);
  const [barCodes, setBarCodes] = useState(['']);
  const [errors, setErrors] = useState<string[]>([]);

  const [isDeletingError, setIsDeletingError] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState<string>('');
  const [goofForAmountChange, setGoodForAmountChange] = useState<TGood | null>(
    null
  );
  const isCashboxEnabled = useIsCashboxEnabled();
  const inputRefs = useRef<Array<RefObject<HTMLInputElement>>>([]);

  const { loadedGoods, createGood, updateGood, deleteGood, updateGoods } =
    useGoodsRequests({ setIsDeletingError, setDeletingMessage });

  const { loadedCategories } = useCategoryRequests({ setIsDeletingError });

  useEffect(() => {
    const good = loadedGoods.find((good) => good.id === paramID);
    setGood(good);
  }, [paramID, good, updateGoods]);

  const navigateFromGoods = (): void => {
    navigate('/goods');
  };

  const handleAddBarcode = (): void => {
    const newBarCodes = [...barCodes, ''];
    setBarCodes(newBarCodes);
    setValue('eans', newBarCodes, { shouldDirty: true });

    inputRefs.current[newBarCodes.length - 1] = React.createRef();

    setTimeout(() => {
      inputRefs.current[newBarCodes.length - 1]?.current?.focus();
    }, 0);
  };

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    // setError,
    setValue,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm<IFormValues>({ mode: 'onChange', defaultValues: DEFAULT_VALUES });

  // const lowStockNotification = watch('low_stock_notification');
  const eans = watch('eans');

  useEffect(() => {
    setBarCodes(eans || ['']);
  }, [eans]);

  useEffect(() => {
    if (good) {
      reset({
        ...DEFAULT_VALUES,
        title: good.title,
        cost: good.cost ?? '',
        amount: good.amount,
        wholesale_cost: good.wholesale_cost ?? '',
        use_global_discounts: good.use_global_discounts,
        comment: good.comment || '',
        use_fair_sign: good.use_fair_sign,
        eans: good.eans ?? [''],
        nds: good.vat ?? null,
        is_excise: good.is_excise,
        image: good.image,
        show_in_shell: good.show_in_shell,
        category: good.category || null,
        low_stock_notification: good.low_stock_notification || null,
        highlighted: good.highlighted || false,
      });

      trigger();

      return;
    }

    reset(DEFAULT_VALUES);
  }, [good, reset, trigger]);

  const size = useWindowResize();
  return (
    <Preloader loading={isGoodDeleting}>
      {isDeletingError && (
        <NotificationDelete
          title={t('good.deleting')}
          description={deletingMessage}
          onClose={() => setIsDeletingError(false)}
        />
      )}
      {goofForAmountChange && (
        <ManagerWarehouseModal
          good={goofForAmountChange}
          onClose={() => setGoodForAmountChange(null)}
        />
      )}
      <form
        className={s.container}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
        onSubmit={handleSubmit(
          ({
            cost,
            amount,
            wholesale_cost,
            show_in_shell,
            category,
            low_stock_notification,
            eans,
            highlighted,
            ...data
          }) => {
            if (good) {
              if (good.in_combo) {
                return updateGoods({
                  ids: [good.id],
                  input: {
                    show_in_shell: show_in_shell,
                    category_id: category ? category.id : null,
                    low_stock_notification: low_stock_notification,
                    highlighted,
                  },
                })
                  .then(navigateFromGoods)
                  .catch((error) => {
                    let message = t('errors.ean.inUse');

                    if (error.message === 'good is used in combo') {
                      message = t('good.combo.error');
                    }

                    notificationService.info({
                      t,
                      type: 'error',
                      title: t('good.update.error'),
                      message,
                    });
                  });
              }

              return updateGood({
                id: good.id,
                ...data,
                cost: cost || 0,
                wholesale_cost: wholesale_cost || 0,
                NDS: data.nds ?? undefined,
                show_in_shell: show_in_shell,
                image: data.image || null,
                category: category || null,
                low_stock_notification: low_stock_notification || null,
                eans: eans.filter(Boolean),
                highlighted,
              })
                .then(navigateFromGoods)
                .catch(({ message }) => {
                  let errorMessage = '';

                  switch (message) {
                    case 'good is used in combo':
                      errorMessage = t('good.combo.error');
                      break;
                    case 'ean is already taken':
                      errorMessage = t('errors.ean.inUse');
                      break;
                  }

                  notificationService.info({
                    t,
                    type: 'error',
                    title: t('good.update.error'),
                    message: errorMessage,
                  });
                });
            }

            return createGood({
              ...data,
              cost: cost || 0,
              wholesale_cost: wholesale_cost || 0,
              amount: amount || 0,
              NDS: data.nds ?? undefined,
              show_in_shell: show_in_shell,
              image: data.image || null,
              category: category || null,
              eans: eans.filter(Boolean),
              low_stock_notification: low_stock_notification || null,
              highlighted,
            })
              .then(navigateFromGoods)
              .catch(({ message }) => {
                let errorMessage = '';

                switch (message) {
                  case 'ean is already taken':
                    errorMessage = t('errors.ean.inUse');
                    break;
                }

                notificationService.info({
                  t,
                  type: 'error',
                  title: t('good.create.error'),
                  message: errorMessage,
                });
              });
          }
        )}
      >
        <div className={s.link_back} onClick={navigateFromGoods}>
          <BackwardIcon />
          {t('back')}
        </div>
        <div>
          <H2>{t(good ? 'good.editing' : 'good.creating')}</H2>
          {good?.in_combo && (
            <InfoBlock
              color="blue"
              iconName="InfoIcon"
              className={s.helper}
              title={t('good.edit.combo.info')}
            />
          )}
        </div>
        <div className={s.content}>
          <div className={s.left_content}>
            <div className={s['fields-container']}>
              <H2 className={s.fields_title}>
                {t('goods.edit.subtitle_basic')}
              </H2>
              <div className={s.row}>
                <H3>{t('title')}</H3>
                <div className={s.fields}>
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        {...field}
                        variant="secondary"
                        error={error?.message}
                        placeholder={t('title.insert')}
                        bordered
                        disabled={good?.in_combo}
                      />
                    )}
                    name="title"
                    control={control}
                    rules={{
                      required: t('errors.required'),
                      minLength: {
                        value: 2,
                        message: t('errors.minLength', 2),
                      },
                      maxLength: {
                        value: 50,
                        message: t('errors.maxLength', 50),
                      },
                    }}
                  />
                </div>
              </div>

              <div className={s.row}>
                <H3>
                  {t('tariff.price')}, {currency.sign}
                </H3>
                <div className={s.fields}>
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <NumberInput
                        {...field}
                        error={error?.message}
                        title={t('good.price.sell.insert')}
                        bordered
                        disabled={good?.in_combo}
                      />
                    )}
                    name="cost"
                    control={control}
                    rules={{
                      required: t('errors.required'),
                      min: {
                        value: 0,
                        message: t('errors.min', 0, currency.sign),
                      },
                      max: {
                        value: 16000000,
                        message: t('errors.max', '16 000 000', currency.sign),
                      },
                    }}
                  />
                  <Controller
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <NumberInput
                        {...field}
                        error={error?.message}
                        title={t('good.price.buy.insert')}
                        bordered
                        disabled={good?.in_combo}
                      />
                    )}
                    name="wholesale_cost"
                    rules={{
                      required: t('errors.required'),
                      min: {
                        value: 0,
                        message: t('errors.min', 0, currency.sign),
                      },
                      max: {
                        value: 16000000,
                        message: t('errors.max', '16 000 000', currency.sign),
                      },
                    }}
                  />
                </div>
              </div>
              {good && (
                <div className={cn(s.row, s.row_amount)}>
                  <H3>{t('amount')}</H3>
                  <div className={cn(s.fields, s.fields_amount)}>
                    <Button
                      variant="ghost"
                      className={cn(s.warehouse, !good && s.disabled)}
                      onClick={() => {
                        if (!good) {
                          return;
                        }
                        setGoodForAmountChange(good);
                      }}
                    >
                      {t('good.amount.arriving')}
                    </Button>
                    <Controller
                      render={({ field }) => (
                        <NumberInput
                          {...field}
                          variant="secondary"
                          disabled
                          className={s.amount}
                          rightContent={`${t('pcs')}`}
                        />
                      )}
                      name="amount"
                      control={control}
                    />
                  </div>
                </div>
              )}

              <div className={cn(s.row, s.row_barcode)}>
                <div className={s.barcode_header}>
                  <H3>{t('ean')}</H3>
                  <div className={cn(s.fields, s.barcode)}>
                    <span className={s.description}>{t('ean.help')}</span>
                  </div>
                </div>
                <div className={s.barcode_body}>
                  {barCodes.map((code, index) => (
                    <Controller
                      key={index}
                      control={control}
                      render={({ field: { onChange, ...field } }) => (
                        <Input
                          {...field}
                          ref={inputRefs.current[index]}
                          value={code || ''}
                          onChange={(value, event) => {
                            if (event.target.validity.valid) {
                              const newBarCodes = [...barCodes];
                              newBarCodes[index] = value;

                              setBarCodes(newBarCodes);
                              setValue('eans', newBarCodes, {
                                shouldDirty: true,
                              });

                              const newErrors = [...errors];
                              if (value.length < 8 || value.length > 16) {
                                newErrors[index] = t(
                                  'good.manager.eans.error.length'
                                );
                              } else if (
                                barCodes.some(
                                  (code, i) => code === value && i !== index
                                )
                              ) {
                                newErrors[index] = t(
                                  'good.manager.eans.error.some'
                                );
                              } else {
                                newErrors[index] = '';
                              }
                              if (value === '') {
                                newErrors[index] = '';
                              }
                              setErrors(newErrors);
                            }
                          }}
                          pattern="\d*"
                          errorWrapperClassName={s.fullWidth}
                          rightContent={
                            index !== 0 ? (
                              <button
                                className={good?.in_combo ? s.eans_delete : ''}
                                disabled={good?.in_combo}
                                onClick={(event) => {
                                  event.preventDefault();
                                  const newBarCodes = barCodes.filter(
                                    (_, i) => i !== index
                                  );
                                  setBarCodes(newBarCodes);
                                  setValue('eans', newBarCodes, {
                                    shouldDirty: true,
                                  });

                                  const newErrors = [...errors];
                                  newErrors.splice(index, 1);
                                  setErrors(newErrors);
                                }}
                              >
                                <BucketIcon color={'secondary'} />
                              </button>
                            ) : (
                              ''
                            )
                          }
                          disabled={good?.in_combo}
                          variant="secondary"
                          error={errors[index]}
                          placeholder={t('ean')}
                          bordered
                        />
                      )}
                      name={'eans'}
                    />
                  ))}

                  {!good?.in_combo && (
                    <Button
                      className={s.barcode_button}
                      variant={'secondary'}
                      iconName="CirclePlusIcon"
                      onClick={handleAddBarcode}
                      disabled={barCodes.length > 9 && true}
                    >
                      {t('add')}
                    </Button>
                  )}
                </div>
              </div>
              {isCashboxEnabled && (
                <>
                  <Controller
                    render={({ field }) => (
                      <Toggle
                        {...field}
                        label={t('goods.edit.honest_sign.label')}
                        description={t('goods.edit.honest_sign.description')}
                        className={s.toggle}
                        variant="secondary"
                        disabled={good?.in_combo}
                      />
                    )}
                    name="use_fair_sign"
                    control={control}
                  />
                  <Controller
                    render={({ field }) => (
                      <Toggle
                        {...field}
                        label={t('goods.edit.excisable.label')}
                        description={t('goods.edit.excisable.description')}
                        className={s.toggle}
                        variant="secondary"
                        rightContent={
                          <Tooltip
                            contentClassName={s.excisable_tolltip}
                            content={t('goods.edit.excisable.tooltip')}
                          />
                        }
                        disabled={good?.in_combo}
                      />
                    )}
                    name="is_excise"
                    control={control}
                  />
                  <div className={cn(s.row, s.row_nds)}>
                    <H3 className={s.nds_title}>{t('goods.edit.nds.label')}</H3>
                    <span className={s.nds_text}>
                      {t('goods.edit.nds.description')}
                    </span>
                    <div className={s.fields}>
                      <Controller
                        render={({ field }) => (
                          <NDSSelect
                            {...field}
                            disabled={good?.in_combo}
                            className={s.nds}
                          />
                        )}
                        name="nds"
                        control={control}
                      />
                    </div>
                  </div>
                </>
              )}

              <H2 className={s.additional_header}>{t('additional')}</H2>

              <div className={s.row}>
                <div className={s.fields}>
                  <Controller
                    render={({ field }) => (
                      <Toggle
                        {...field}
                        className={s.toggle_discount}
                        label={t('tariff.discount.apply')}
                        disabled={good?.in_combo}
                        variant="secondary"
                        emptyBg
                        iconName="PercentIcon"
                        iconPosition="left"
                      />
                    )}
                    name="use_global_discounts"
                    control={control}
                  />
                </div>
              </div>

              <div className={cn(s.row)}>
                <H3 className={s.nds_title}>
                  {t('goods.edit.select_group.placeholder')}
                </H3>
                <div className={cn(s.fields, s.fields_select)}>
                  <Controller
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder={t('goods.edit.select_group.placeholder')}
                        emptyOptionsPlaceholder={t(
                          'goods.select.empty_options'
                        )}
                        options={loadedCategories.map((category) => ({
                          title: category.title,
                          value: category.id,
                        }))}
                        className={s.select_group}
                        value={field.value ? field.value.id : null}
                        bordered
                        onChange={(newValue) => {
                          const selectedCategory = loadedCategories.find(
                            (category) => category.id === newValue
                          );
                          field.onChange(selectedCategory);
                        }}
                      />
                    )}
                    name="category"
                    control={control}
                  />
                </div>
              </div>

              <div className={cn(s.row, s.row_separator)}>
                <Controller
                  render={({ field }) => (
                    <Toggle
                      {...field}
                      className={s.toggle_showShell}
                      label={t('goods.edit.show_in_shell.label')}
                      description={
                        <>
                          {t('goods.edit.show_in_shell.description_1')}{' '}
                          <NavLink
                            state={{ selectedTab: 'SHELL' }}
                            to={'/settings'}
                            className={s.link}
                          >
                            {t('goods.edit.show_in_shell.description_2')}
                          </NavLink>{' '}
                          {t('goods.edit.show_in_shell.description_3')}
                        </>
                      }
                      variant="secondary"
                    />
                  )}
                  name="show_in_shell"
                  control={control}
                />
              </div>

              <FeatureHOC
                alias="telegram_notify"
                strategy="hide"
                name={t('setting.notifications.telegram')}
                grayBG
              >
                <div className={cn(s.row, s.row_separator)}>
                  <Controller
                    render={({ field }) => (
                      <Toggle
                        {...field}
                        value={field.value || false}
                        className={s.toggle_notify}
                        label={t('goods.edit.telegram.label')}
                        rightContent={
                          <Tooltip
                            content={t('good.edit.telegram.tooltip')}
                            contentClassName={s.tooltip}
                            position={
                              size.width > 960 ? 'bottom' : 'bottom-right'
                            }
                          />
                        }
                        description={
                          <>
                            {!telegramChannelId?.id && (
                              <>
                                {t('good.edit.telegram.text_1')}{' '}
                                <NavLink
                                  state={{ selectedTab: 'INTEGRATIONS' }}
                                  to={'/settings'}
                                  className={s.link}
                                >
                                  {t('goods.edit.telegram.description_2')}
                                </NavLink>
                              </>
                            )}
                          </>
                        }
                        variant="secondary"
                        disabled={!telegramChannelId?.id}
                      />
                    )}
                    name="low_stock_notification.enabled"
                    control={control}
                  />
                  <div className={s.row_block}>
                    <Separator className={s.separation} />
                    <div className={cn(s.field, s.field_value)}>
                      <span className={s.notify_value}>
                        {t('goods.manager.telegram.rest')}
                      </span>
                      <Controller
                        render={({ field, fieldState: { error } }) => (
                          <NumberInput
                            {...field}
                            value={
                              good?.low_stock_notification?.threshold || ''
                            }
                            variant="secondary"
                            error={error?.message}
                            className={s.amount}
                            bordered
                            disabled={!telegramChannelId?.id}
                            rightContent={`${t('pcs')}.`}
                            placeholder="5"
                          />
                        )}
                        name="low_stock_notification.threshold"
                        control={control}
                        rules={{
                          min: {
                            value: 1,
                            message: t('errors.min', 1, t('pcs')),
                          },
                          max: {
                            value: 999,
                            message: t('errors.max', '999', t('pcs')),
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </FeatureHOC>

              <div className={cn(s.row, s.row_separator)}>
                <div className={cn(s.field, s.field_showShell)}>
                  <H3 className={s.comment_title}>
                    {t('goods.edit.textarea.label')}
                  </H3>
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <TextArea
                        title={''}
                        error={error?.message}
                        variant={'secondary'}
                        placeholder={t('goods.edit.textarea.placeholder')}
                        {...field}
                        bordered
                        limit={255}
                        className={cn(s.textarea, good?.in_combo && s.disabled)}
                      />
                    )}
                    name="comment"
                    control={control}
                    rules={{
                      minLength: {
                        value: 2,
                        message: t('errors.minLength', 2),
                      },
                      maxLength: {
                        value: 255,
                        message: t('errors.maxLength', 255),
                      },
                    }}
                  />
                </div>
              </div>

              <div className={cn(s.row, s.row_separator)}>
                <Controller
                  render={({ field }) => (
                    <Toggle
                      label={t('goods.edit.highlighted.label')}
                      description={t('goods.edit.highlighted.description')}
                      {...field}
                      iconName="StarsIcon"
                      variant="secondary"
                      iconPosition="left"
                    />
                  )}
                  control={control}
                  name="highlighted"
                />
              </div>
            </div>
            <div
              className={cn(
                s['submit-container'],
                s['submit-container_double']
              )}
            >
              {good && (
                <Button
                  variant="outline"
                  color="red"
                  fullWidth
                  disabled={good?.in_combo}
                  onClick={() =>
                    openConfirmDialog(t, 'remove', {
                      title: t('good.deleting'),
                      message: t('good.deleting.confirm', good.title),
                    }).then(({ status }) => {
                      if (status === 'ACCEPTED') {
                        setIsGoodDeleting(true);
                        return deleteGood(good.id)
                          .catch(() => {
                            setIsDeletingError(true);
                            return Promise.reject();
                          })
                          .finally(() => {
                            if (good?.amount === 0) {
                              navigateFromGoods();
                            }
                            setIsGoodDeleting(false);
                          });
                      }
                    })
                  }
                >
                  {t('delete')}
                </Button>
              )}

              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={!isDirty || errors.some((error) => error)}
                fullWidth
                className={!good ? s.create : undefined}
              >
                {t(good ? 'save' : 'create')}
              </Button>
            </div>
          </div>
          <div className={s.right_content}>
            <EditPhoto disabled={good?.in_combo} control={control} />
          </div>
        </div>
      </form>
    </Preloader>
  );
};
